import { PageSkeleton } from "./shared/PageSkeleton";
import { Suspense, useEffect, useMemo, useState } from "react";
import { Routes, Navigate, Route, useLocation, useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "@skypointcloud/skypoint-ui";
import HeaderSky from "../views/dashboard/header/header";
import SidebarSky from "../views/dashboard/sidebar/sidebar";
import SuccessAndErrorMessage from "./successAndErrorMessage/successAndErrorMessage";
import ErrorBoundary from "./shared/ErrorBoundary";
import RouteErrorBoundary from "./shared/RouteErrorBoundary";
import InactivePlan from "./inactiveplan";
import { IRouteDetails } from "../common/App.interface";
import { createAppRoutes } from "../routes/appRoutes";
import { SidebarProvider } from "../contexts/SidebarContext";
import { DisplayTzLoader } from "./DisplayTzLoader";
import { BulkEmailTracker } from "./bulkEmail/BulkEmailTracker";
import { useActivityTracking } from "../hooks/useActivityTracking";
import { useGetMyPermissionsQuery } from "../store/api/rbacApi";
import { useGetMyAccessScopeQuery } from "../store/api/accessScopeApi";
import { useGetEngagementEnabledQuery } from "@/store/api/candidateEngagementApi";
import { IRootReducerState } from "../store/IRootReducer";
import {
  buildNavSections,
  isSharedUsersPath,
  needsPerformanceNavContext,
  sharedUsersProductOf,
  usePerformanceNavContext,
  type NavId,
} from "../views/dashboard/sidebar/sidebarConfig";
import { getNavRoute } from "../views/dashboard/sidebar/routeMapping";
import { getInstanceIdFromUrl } from "../utils/instanceId";
import { useEnabledProducts } from "../hooks/useEnabledProducts";
import {
  ActiveProductContext,
  activeProductFor,
  useActiveProductState,
  useActiveProductSync,
  type ActiveProduct,
} from "../hooks/useActiveProduct";
import { useProductThemeAttribute } from "./products/productTheme";
import { skipsHireShellReads } from "./products/activeProduct";
import { isSharedPath } from "../routes/sharedPaths";
import { DEFAULT_PRODUCT, PRODUCT_LABELS, PRODUCT_LANDING_PATH } from "../../shared/products/keys";
import { clearProduct } from "../store/slices/authSlice";
import { refusedRouteRedirect, routeAllowed } from "./routeGuardRules";
import {
  consumeProductRevocation,
  getProductRevocation,
  useProductRevocation,
} from "./access/productRevocation";
import { reportAccessDenial } from "./access/accessDenial";
import { SettingsBackLink } from "./SettingsBackLink";

interface AppLayoutProps {
  isFullAccess: boolean;
  windowWidth: number | null;
  notificationMessage: string;
  setSessionDefault: (value: any) => void;
}

/**
 * Checks if a route should be rendered based on its accessibility conditions
 */
function shouldRenderRoute(route: IRouteDetails): boolean {
  return (
    !route.accessibilityConditions ||
    (route.accessibilityConditions?.length > 0 &&
      route.accessibilityConditions?.some((condition) => condition === true))
  );
}

/**
 * Redirects users away from routes they don't have permission to access.
 * Builds the same nav sections as the sidebar, collects allowed route bases,
 * and redirects to the first allowed route if the current path is unauthorized.
 *
 * UI only: it decides what the browser shows, never what the caller may do. Every hire router
 * and every performance route on the server gates on its own.
 *
 * `admitted` is true once the guard has decided and the page stays; only then may the active
 * product remember the page (useActiveProductSync).
 *
 * `hold` is true while a non-default product's page (today `/performance/*`) must not render: its
 * products, permissions or performance context have not resolved, or the route is refused and
 * the redirect is on its way. Fail closed: such a page never mounts before the guard admits it.
 * The same holds for every page on a performance-only instance (Task 17 ruling 5), whose hire
 * pages go to the landing. Otherwise always false on a hire route, so hire pages render exactly as
 * they did.
 *
 * Admission is decided on the URL's own product, exactly as before. Only where a refused shared
 * page (SHARED_PATHS) sends the user follows the active product: someone in Performance goes to
 * Performance's landing, not into hire. That differs from before only for a user holding more than
 * one product, whose active product there can be another one than the URL's.
 */
function useRouteGuard(active: Pick<ActiveProduct, "product" | "held">): {
  hold: boolean;
  admitted: boolean;
} {
  const location = useLocation();
  const navigate = useNavigate();
  const authState = useSelector((state: IRootReducerState) => state.authReducer);
  const instanceName = useSelector(
    (state: IRootReducerState) => state.instancesReducer.instancesData.selectedInstanceName,
  );
  const instanceId = getInstanceIdFromUrl();
  const activeProduct = activeProductFor(location.pathname);
  const isProductRoute = activeProduct !== DEFAULT_PRODUCT;
  const { products, ready: productsReady, has, holdsRole } = useEnabledProducts();
  const {
    data: myPerms,
    isLoading,
    isError: permsFailed,
  } = useGetMyPermissionsQuery(instanceId ?? undefined);

  // Same flag the sidebar uses, so the route guard's allow-list matches what
  // is actually rendered. Without it the guard would redirect away from the
  // engagement pages even when the tenant has the feature on.
  // On an IT page for someone with no hire role, hire's reads are refused by the server: not made.
  const skipHireReads = skipsHireShellReads(activeProduct, holdsRole(DEFAULT_PRODUCT));
  const { data: engagementEnabled } = useGetEngagementEnabledQuery(undefined, {
    skip: skipHireReads,
  });

  // The sidebar hides the Inbox from data scopes the server refuses it to; the
  // guard reads the same scope (one shared cache entry) so /inbox follows suit.
  const { data: accessScope } = useGetMyAccessScopeQuery(instanceId || "", {
    skip: !instanceId || skipHireReads,
  });
  const dataScope = accessScope?.dataScope;

  // The shared Users page also opens to the performance users permission (spec §5), on an
  // instance with that product only; null on any other page or instance.
  const onUsersPage = isSharedUsersPath(location.pathname);
  const usersProduct = onUsersPage ? sharedUsersProductOf(products) : null;

  // Loaded only when the URL is in a non-default product the instance has, or on the Users page of
  // an instance with performance; otherwise skipped and ready.
  const performance = usePerformanceNavContext(
    instanceId,
    (isProductRoute && needsPerformanceNavContext(activeProduct) && has(activeProduct)) ||
      usersProduct !== null,
    myPerms?.permissions?.["users.manage"] === true,
  );

  const holdsActiveProduct = holdsRole(activeProduct);
  const sections = useMemo(
    () =>
      buildNavSections(
        {
          isFullAccess: authState.isFullAccess,
          isTenantAdmin: authState.permissions.isTenantAdmin,
          isPlatformAdmin: authState.permissions.isPlatformAdmin,
          isInstanceAdmin: authState.permissions.isInstanceAdmin,
          rbacNavigation: myPerms?.navigation,
          rbacPermissions: myPerms?.permissions,
          // Pass the resolved role name so role-name-gated nav items are included
          // in the route guard's allow-list — otherwise the guard redirects them
          // away on direct nav.
          userRoleName: myPerms?.role ?? null,
          instanceName,
          instanceId,
          dataScope,
          isSkyAgentEnabled: authState.capabilities.skyAgentEnabled,
          // The guard, unlike the sidebar, does not wait for SharePoint to be
          // connected: /document-centre stays reachable by its address and
          // shows its own full-page state (an admin following "set it up", or
          // a bookmark after a disconnect, must not be bounced to Applicants).
          // Who may open it at all is still decided by the rest of the entry's
          // gate.
          isSharePointConnected: true,
        },
        engagementEnabled ?? false,
        {
          activeProduct,
          enabledProducts: products,
          performance,
          // skyPerformance IT's sidebar needs only whether an IT role is held (the session's).
          performanceIt: { held: holdsActiveProduct },
        },
      ),
    [
      engagementEnabled,
      dataScope,
      authState.isFullAccess,
      authState.permissions,
      authState.capabilities.skyAgentEnabled,
      myPerms,
      instanceName,
      instanceId,
      activeProduct,
      products,
      performance,
      holdsActiveProduct,
    ],
  );

  // A performance-only instance, known once products resolved (Task 17 ruling 5). Its hire pages
  // never render: they are held on the loader and sent to the landing. False on every instance with
  // hire (and a legacy empty list), and before products resolve, so those decide exactly as before.
  const hireOff = productsReady && !has(DEFAULT_PRODUCT);
  // Hire permissions answered. On a product route a failed request counts as an empty answer, so
  // the guard still decides there (the hire grants only feed the shared Users link) instead of
  // holding the page on the loader for good; likewise on a performance-only instance, where a hire
  // page is refused whatever they say. Other hire routes keep waiting for an answer, as before.
  const permsAnswered = !isLoading && (!!myPerms || ((isProductRoute || hireOff) && permsFailed));
  const allowed =
    permsAnswered &&
    routeAllowed(
      location.pathname,
      sections,
      myPerms?.permissions ?? {},
      usersProduct !== null && performance.managesPerformanceUsers,
      !hireOff,
    );
  // Never redirect mid-load (spec §6). A product route waits for its products and context. The
  // Users page waits for them only when the hire rule has refused it, since the performance
  // permission may still admit it; a page the hire rule admits is decided exactly when it was.
  // Every other hire route depends on none of it.
  const productPending = !productsReady || !performance.ready;
  // A refused shared page (Users, Products, Account, ...) waits too: where it sends the user
  // depends on the active product, which is not known before products resolve (review M-4).
  const onSharedPage = isSharedPath(location.pathname) && location.pathname !== "/";
  const waiting = isProductRoute
    ? productPending
    : (onUsersPage || onSharedPage) && !allowed && productPending;
  const settled = permsAnswered && !waiting;

  useEffect(() => {
    if (!settled || allowed) return;
    const pathname = location.pathname;
    const queryString = window.location.search;
    if (hireOff && !isProductRoute) {
      // A hire page on a performance-only instance: the landing, never another hire page. The
      // router's own search (the same string under BrowserRouter), so `?instanceid` survives.
      if (pathname !== "/") navigate("/" + location.search, { replace: true });
      return;
    }
    if (
      isSharedPath(pathname) &&
      pathname !== "/" &&
      active.product !== activeProduct &&
      active.held.includes(active.product)
    ) {
      // A shared page refused while the user is in another product: that product's landing.
      navigate(PRODUCT_LANDING_PATH[active.product] + location.search, { replace: true });
      return;
    }
    // #1435/#1437: an employee's evaluation address opens as their own, the list as My file's
    // Evaluations tab, rather than the first item (Home). Undefined everywhere else.
    const instead = isProductRoute
      ? refusedRouteRedirect(pathname, location.search, sections)
      : undefined;
    if (instead) {
      navigate(instead, { replace: true });
      return;
    }
    if (sections.length > 0) {
      const route = getNavRoute(sections[0].items[0].id as NavId);
      // Never bounce to the page we were just refused from.
      if (route !== pathname) navigate(route + queryString, { replace: true });
    } else if (pathname !== "/") {
      navigate("/" + queryString, { replace: true });
    }
  }, [
    location.pathname,
    location.search,
    sections,
    settled,
    allowed,
    navigate,
    hireOff,
    isProductRoute,
    active.product,
    active.held,
    activeProduct,
  ]);

  return {
    hold: (isProductRoute || hireOff) && !(settled && allowed),
    // The guard has decided and the page stays: the active product may remember it.
    admitted: settled && allowed,
  };
}

/**
 * A product disabled mid-session (spec §6): drop it, leave it, say so once.
 *
 * Where to: hire, when the instance has hire and the user holds a hire role. Otherwise hire would
 * be a dead end (a performance-only instance, or nothing of the user's in hire), so the
 * NO_PRODUCT_ACCESS screen instead, which replaces the app and offers Switch instance and Sign out.
 * Decided from the products as they were before the clear: dropping the only product leaves an
 * empty list, which reads as a legacy hire instance.
 *
 * UI only: the server already refused the calls that reported it.
 */
export function useProductRevocationHandler(): void {
  const revoked = useProductRevocation();
  const dispatch = useDispatch();
  const capabilities = useSelector((state: IRootReducerState) => state.authReducer.capabilities);
  const navigate = useNavigate();
  const toast = useToast();
  useEffect(() => {
    // The store, not the render's snapshot: an effect run again (StrictMode, or a capabilities
    // change in the same commit) after the consume below must not toast and dispatch twice.
    if (!revoked || getProductRevocation() !== revoked) return;
    const { products, productRoles } = capabilities;
    const instanceHasHire = products.length === 0 || products.includes(DEFAULT_PRODUCT);
    const holdsHire = Boolean(productRoles[DEFAULT_PRODUCT]);
    dispatch(clearProduct(revoked));
    toast.info(`${PRODUCT_LABELS[revoked]} is no longer available on this workspace.`);
    if (instanceHasHire && holdsHire) {
      navigate(PRODUCT_LANDING_PATH[DEFAULT_PRODUCT] + window.location.search, { replace: true });
    } else {
      reportAccessDenial({ code: "NO_PRODUCT_ACCESS", instanceId: getInstanceIdFromUrl() });
    }
    consumeProductRevocation();
  }, [revoked, capabilities, dispatch, navigate, toast]);
}

/**
 * Main application layout component containing header, sidebar, and content area
 * Pattern: Matches skypoint-skyprivacy's AppLayout architecture
 */
export function AppLayout({
  isFullAccess,
  windowWidth,
  notificationMessage,
  setSessionDefault,
}: AppLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useActivityTracking();
  useProductRevocationHandler();
  // The active product (skyAdvisor's PackContext): resolved once here for the whole shell, and
  // stored when a product's own page forces it.
  const active = useActiveProductState();
  const { hold: holdProductRoute, admitted } = useRouteGuard(active);
  useActiveProductSync(active, admitted);
  // The product's palette (productTheme.ts): `data-product` on <html> while the shell draws a
  // themed product, absent on hire. Follows the shell's product, as the sidebar and wordmark do.
  useProductThemeAttribute(active.product);
  // The catch-all below keeps the query string, so `?instanceid` survives an unknown path (U6).
  const { search } = useLocation();

  const routes = useMemo(() => createAppRoutes({ setSessionDefault }), [setSessionDefault]);

  const filteredRoutes = useMemo(
    () => routes.filter((route) => shouldRenderRoute(route)),
    [routes],
  );

  if (!isFullAccess) {
    return <InactivePlan />;
  }

  const shell = (
    <SidebarProvider collapsed={sidebarCollapsed}>
      {/* Renders nothing. Loads the viewer's display timezone into the store once,
          so useDisplayTz() resolves everywhere without each screen fetching it.
          A component rather than a hook call here, so it cannot be caught by the
          early return above it. An inactive-plan user never reaches this, which
          is fine: that screen shows no timestamps. */}
      <DisplayTzLoader />
      {/* Follows bulk email sends after the Send Email dialog closes: progress,
          then the result. Here so it survives navigating between pages. */}
      <BulkEmailTracker />
      <HeaderSky productAdmitted={admitted} />
      <ErrorBoundary>
        <SidebarSky onCollapseChange={setSidebarCollapsed} />
        <div
          className={`content-wrapper min-h-[95vh]! bg-surface-subtle dark:bg-background transition-[margin] duration-200 ${
            sidebarCollapsed ? "md:ml-12!" : "md:ml-[260px]!"
          }`}
        >
          <SuccessAndErrorMessage
            notificationMessage={notificationMessage}
            successClassName="successMessageDashboard"
            failureClassName="errorMessageDashboard"
            warningClassName="warningMessageDashboard"
          />
          {/* D5 (2026-10-08 shell spec): "Back to Settings" above a settings page, once the guard
              has admitted it, so a refused page never shows one on its way out. */}
          {admitted && <SettingsBackLink />}
          <Suspense
            // A page-shaped placeholder while the route's code loads, not a
            // spinner on a blank canvas.
            fallback={<PageSkeleton />}
          >
            {holdProductRoute ? (
              // Held until the route guard decides (product routes, performance-only instances):
              // the same page-shaped placeholder as a route whose code is still loading.
              <PageSkeleton />
            ) : (
              <Routes>
                {filteredRoutes.map((route) => (
                  <Route
                    key={route.name}
                    path={route.path}
                    element={
                      <RouteErrorBoundary fallbackTitle={`Error loading ${route.name}`}>
                        <route.component
                          realTimeData={route.isRealTimeDataOn ? route.realTimeData : undefined}
                          resetRealTimeData={
                            route.isRealTimeDataOn && route.resetRealTimeData
                              ? route.resetRealTimeData
                              : undefined
                          }
                          windowWidth={windowWidth}
                          {...route.extraProps}
                        />
                      </RouteErrorBoundary>
                    }
                  />
                ))}
                <Route
                  path="*"
                  element={
                    <Navigate
                      to={{ pathname: "/", search }}
                      replace
                    />
                  }
                />
              </Routes>
            )}
          </Suspense>
        </div>
      </ErrorBoundary>
    </SidebarProvider>
  );

  return (
    <ActiveProductContext.Provider value={active.product}>{shell}</ActiveProductContext.Provider>
  );
}

export default AppLayout;
